/**
 * Persistent expiration-count selector.
 *
 * The backend fetches a generous window of options chains (12 weeks
 * ≈ 10-12 expiries) so the frontend can slice that down to N=3/5/10
 * client-side without a roundtrip on every flip. Default 5 — same as
 * AION's typical front-cluster view, dense enough to see weekly +
 * monthly cadence without crowding the heatmap.
 *
 * Values:
 *   3   →  show nearest 3 expirations
 *   5   →  show nearest 5 expirations (default)
 *   10  →  show nearest 10 expirations
 *   0   →  show ALL fetched expirations
 *
 * Stored in localStorage under STORAGE_KEYS.expLimit; cross-tab sync
 * via the `storage` event so flipping in one tab updates another.
 */

import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../lib/constants';

export type ExpirationLimit = 3 | 5 | 10 | 0;
const VALID: ExpirationLimit[] = [3, 5, 10, 0];
const DEFAULT: ExpirationLimit = 5;

function readStored(): ExpirationLimit {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.expLimit);
    const n = raw == null ? NaN : Number(raw);
    if (VALID.includes(n as ExpirationLimit)) return n as ExpirationLimit;
  } catch { /* localStorage unavailable */ }
  return DEFAULT;
}

export function useExpirationLimit() {
  const [limit, setLimit] = useState<ExpirationLimit>(() => readStored());

  const update = useCallback((next: ExpirationLimit) => {
    if (!VALID.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEYS.expLimit, String(next)); }
    catch { /* ignore */ }
    setLimit(next);
  }, []);

  // Cross-tab sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEYS.expLimit) return;
      setLimit(readStored());
    };
    globalThis.addEventListener('storage', onStorage);
    return () => globalThis.removeEventListener('storage', onStorage);
  }, []);

  return [limit, update] as const;
}
